import React, { useEffect, useRef, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { Backdrop, CircularProgress } from "@mui/material";
import RemoveRedEyeIcon from "@mui/icons-material/RemoveRedEye";
import TuneIcon from "@mui/icons-material/Tune";
import InsightsIcon from "@mui/icons-material/Insights";
import CloseIcon from "@mui/icons-material/Close";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import Inventory2OutlinedIcon from "@mui/icons-material/Inventory2Outlined";
import UnarchiveOutlinedIcon from "@mui/icons-material/UnarchiveOutlined";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faChevronLeft,
  faChevronRight,
} from "@fortawesome/free-solid-svg-icons";
import { DateRangePicker } from "react-date-range";
import "react-date-range/dist/styles.css";
import "react-date-range/dist/theme/default.css";
import dayjs from "dayjs";
import SideNavLayout from "../../Sidenav/SideNavLayout";
import api from "../../api/api";
import { calendericonblack } from "../../utils/svgs/svgs";
import SubcontractorDetailModal from "./SubcontractorDetailModal";
import { SUBCONTRACTOR_ROUTES } from "./routes";
import ToastMessage from "../../utils/muitoast";

// Mirrors the pattern used in viewAiF.jsx: files stored on disk under
// /var/www/hub2.ntactops.com/ntact/public/assets/... are served publicly at
// https://hub2.ntactops.com/ntact/files/... . Images are fetched through the
// backend's CORS proxy so <img src> works across origins.
const DOCUMENT_PUBLIC_BASE = "https://hub2.ntactops.com/ntact/files";
const DOCUMENT_PROXY_BASE =
  "https://ntactapi.brainymed.com/ntact/v1/forms/proxy?url=";

const GRADE_OPTIONS = ["A", "B", "C"];
const APPROVAL_OPTIONS = ["Approved", "Conditional Approval", "Not Approved"];
const STATUS_OPTIONS = [
  { code: "D", label: "Draft" },
  { code: "A", label: "Submitted" },
  { code: "R", label: "Resubmit" },
];

// Map a stored status code to its human-readable label.
const statusLabelOf = (code) =>
  (STATUS_OPTIONS.find((s) => s.code === code) || {}).label || "Draft";

const gradeBadge = (grade) => {
  if (grade === "A") return "bg-green-100 text-green-800 border-green-200";
  if (grade === "B") return "bg-yellow-100 text-yellow-800 border-yellow-200";
  if (grade === "C") return "bg-red-100 text-red-800 border-red-200";
  return "bg-gray-100 text-gray-500 border-gray-200";
};

const approvalBadge = (status) => {
  if (status === "Approved") return "bg-green-600 text-white";
  if (status === "Conditional Approval") return "bg-yellow-500 text-white";
  if (status === "Not Approved") return "bg-red-600 text-white";
  return "bg-gray-300 text-gray-700";
};

// Admin decision badge — set by a reviewer via the detail modal's
// "Admin Approval" tab. Values: "Approved" | "Declined" | "Needs More Info".
const adminApprovalBadge = (status) => {
  if (status === "Approved") return "bg-green-600 text-white";
  if (status === "Declined") return "bg-red-600 text-white";
  if (status === "Needs More Info") return "bg-amber-500 text-white";
  return "bg-gray-300 text-gray-700";
};

// Insurance certificate expiry badge — red when past due, amber within 30 days,
// otherwise neutral. The value is captured on the Attestation page when the
// "Certificate Of Insurance Details" enclosed-document is checked.
const insuranceExpiryInfo = (dateStr) => {
  if (!dateStr) return null;
  const d = dayjs(dateStr);
  if (!d.isValid()) return null;
  const days = d.diff(dayjs(), "day");
  let tone = "bg-green-100 text-green-800 border-green-200";
  let hint = `${days} days left`;
  if (days < 0) {
    tone = "bg-red-100 text-red-800 border-red-200";
    hint = `Expired ${Math.abs(days)}d ago`;
  } else if (days <= 30) {
    tone = "bg-amber-100 text-amber-800 border-amber-200";
    hint = `${days}d left`;
  }
  return { tone, hint, dateLabel: d.format("MM-DD-YYYY") };
};

const statusBadge = (s) => {
  if (s === "A") return "bg-blue-100 text-blue-800 border-blue-200";
  if (s === "R") return "bg-amber-100 text-amber-800 border-amber-200";
  return "bg-gray-100 text-gray-700 border-gray-200";
};

const SubcontractorDashboard = () => {
  const navigate = useNavigate();

  // Project Managers see a read-only dashboard — no scaling controls and no
  // way to surface archived records.
  const currentUser = useSelector((state) => state.user.userDetails);
  const userRole =
    currentUser?.user_roles?.role || currentUser?.role_name || "";
  const isProjectManager = userRole === "Project Manager";

  const [rows, setRows] = useState([]);
  const [loader, setLoader] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");

  const [showPicker, setShowPicker] = useState(false);
  const [showFilterPopup, setShowFilterPopup] = useState(false);
  const pickerRef = useRef(null);
  const filterRef = useRef(null);

  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const [selectedRow, setSelectedRow] = useState(null);
  // Which tab the detail modal should open on when it first mounts.
  const [initialTab, setInitialTab] = useState("form");

  // --- Analytics popup -----------------------------------------------------
  const [showAnalytics, setShowAnalytics] = useState(false);

  const analytics = useMemo(() => {
    const total = rows.length;
    const submitted = rows.filter((r) => r.status === "A").length;
    const drafts = rows.filter((r) => r.status !== "A").length;

    const gradeCounts = { A: 0, B: 0, C: 0, none: 0 };
    const approvalCounts = {
      Approved: 0,
      "Conditional Approval": 0,
      "Not Approved": 0,
      none: 0,
    };
    const adminCounts = {
      Approved: 0,
      Declined: 0,
      "Needs More Info": 0,
      none: 0,
    };
    let scoreSum = 0;
    let scoreN = 0;
    let scoreMin = null;
    let scoreMax = null;
    rows.forEach((r) => {
      const g = r.scorecard?.grade;
      if (g === "A" || g === "B" || g === "C") gradeCounts[g] += 1;
      else gradeCounts.none += 1;

      const ap = r.scorecard?.approval_status;
      if (ap && approvalCounts[ap] !== undefined) approvalCounts[ap] += 1;
      else approvalCounts.none += 1;

      const ad = r.admin_approval;
      if (ad && adminCounts[ad] !== undefined) adminCounts[ad] += 1;
      else adminCounts.none += 1;

      const t = r.scorecard?.total_score;
      if (typeof t === "number") {
        scoreSum += t;
        scoreN += 1;
        scoreMin = scoreMin === null ? t : Math.min(scoreMin, t);
        scoreMax = scoreMax === null ? t : Math.max(scoreMax, t);
      }
    });
    const avgScore = scoreN ? Math.round(scoreSum / scoreN) : null;

    // Insurance buckets
    const today = dayjs();
    let insExpired = 0;
    let insSoon = 0;
    let insValid = 0;
    let insMissing = 0;
    rows.forEach((r) => {
      const d = r.attestation?.insuranceExpiryDate;
      if (!d) {
        insMissing += 1;
        return;
      }
      const days = dayjs(d).diff(today, "day");
      if (isNaN(days)) insMissing += 1;
      else if (days < 0) insExpired += 1;
      else if (days <= 30) insSoon += 1;
      else insValid += 1;
    });

    // Submissions per month (last 6)
    const months = [];
    for (let i = 5; i >= 0; i -= 1) {
      const m = dayjs().subtract(i, "month");
      months.push({ key: m.format("YYYY-MM"), label: m.format("MMM YY"), count: 0 });
    }
    const monthIdx = Object.fromEntries(months.map((m, i) => [m.key, i]));
    rows.forEach((r) => {
      if (!r.created_at) return;
      const k = dayjs(r.created_at).format("YYYY-MM");
      if (monthIdx[k] !== undefined) months[monthIdx[k]].count += 1;
    });
    const monthMax = Math.max(1, ...months.map((m) => m.count));

    // Top companies
    const compMap = {};
    rows.forEach((r) => {
      const c = (r.company_name || "").trim();
      if (!c) return;
      compMap[c] = (compMap[c] || 0) + 1;
    });
    const topCompanies = Object.entries(compMap)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 5);

    // Score histogram (10-point buckets)
    const scoreBuckets = Array.from({ length: 10 }, (_, i) => ({
      key: `${i * 10}-${i * 10 + 9}`,
      label: `${i * 10}-${i * 10 + 9}`,
      count: 0,
    }));
    rows.forEach((r) => {
      const t = r.scorecard?.total_score;
      if (typeof t === "number") {
        const idx = Math.min(9, Math.max(0, Math.floor(t / 10)));
        scoreBuckets[idx].count += 1;
      }
    });
    const scoreBucketMax = Math.max(1, ...scoreBuckets.map((b) => b.count));

    // HSE STATS aggregates
    const toNum = (v) => {
      if (v === null || v === undefined || v === "") return null;
      const n = Number(v);
      return isNaN(n) ? null : n;
    };
    const yearKeys = ["2023", "2024", "2025"];
    const trirByYear = yearKeys.map((y) => {
      let sum = 0;
      let n = 0;
      rows.forEach((r) => {
        const v = toNum(r.hsestats?.trir?.[y]);
        if (v !== null) {
          sum += v;
          n += 1;
        }
      });
      return { year: y, avg: n ? +(sum / n).toFixed(2) : 0, n };
    });
    const trirMax = Math.max(0.01, ...trirByYear.map((t) => t.avg));
    const manhoursByYear = yearKeys.map((y) => {
      let sum = 0;
      rows.forEach((r) => {
        const v = toNum(r.hsestats?.total_manhours?.[y]);
        if (v !== null) sum += v;
      });
      return { year: y, total: sum };
    });
    const lwcByYear = yearKeys.map((y) => {
      let sum = 0;
      rows.forEach((r) => {
        const v = toNum(r.hsestats?.lost_workday_cases?.[y]);
        if (v !== null) sum += v;
      });
      return { year: y, total: sum };
    });

    let emrSum = 0;
    let emrN = 0;
    let totalFatalities = 0;
    let totalSerious = 0;
    rows.forEach((r) => {
      const e = toNum(r.hsestats?.emr);
      if (e !== null) {
        emrSum += e;
        emrN += 1;
      }
      const f = toNum(r.hsestats?.fatalities_last_3_years);
      if (f !== null) totalFatalities += f;
      const s = toNum(r.hsestats?.serious_incidents_last_3_years);
      if (s !== null) totalSerious += s;
    });
    const avgEmr = emrN ? +(emrSum / emrN).toFixed(2) : null;

    return {
      scoreBuckets,
      scoreBucketMax,
      trirByYear,
      trirMax,
      manhoursByYear,
      lwcByYear,
      avgEmr,
      totalFatalities,
      totalSerious,
      yearKeys,
      total,
      submitted,
      drafts,
      gradeCounts,
      approvalCounts,
      adminCounts,
      avgScore,
      scoreMin,
      scoreMax,
      insExpired,
      insSoon,
      insValid,
      insMissing,
      months,
      monthMax,
      topCompanies,
    };
  }, [rows]);

  // --- Insurance scaling (admin-editable coverage minimums) ----------------
  const [showInsurance, setShowInsurance] = useState(false);
  const [insurance, setInsurance] = useState({
    minimums: {},
    labels: {},
    limit_doc: null,
  });
  const [insuranceSaving, setInsuranceSaving] = useState(false);
  const [insuranceMsg, setInsuranceMsg] = useState("");
  // Tracks the in-flight upload of the "Required Insurance Limits" document.
  const [limitDocUploading, setLimitDocUploading] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const resp = await api.get("/v1/subcontractor/insurance_scaling");
        const data = resp?.data?.data || resp?.data;
        if (data) setInsurance(data);
      } catch (err) {
        console.warn("Could not load insurance scaling config", err);
      }
    })();
  }, []);

  // Upload the limits reference doc, then stash {file_name, file_path} on the
  // insurance state. It only persists once the admin clicks "Save".
  const handleLimitDocUpload = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = ""; // allow re-selecting the same file
    if (!file) return;
    try {
      setLimitDocUploading(true);
      setInsuranceMsg("");
      const fd = new FormData();
      fd.append("files", file);
      const resp = await api.post("/v1/forms/upload-files", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const uploaded =
        (Array.isArray(resp?.files) && resp.files[0]) ||
        (Array.isArray(resp?.data?.files) && resp.data.files[0]) ||
        null;
      if (uploaded) {
        setInsurance((s) => ({ ...s, limit_doc: uploaded }));
      } else {
        setInsuranceMsg("Upload failed — no file returned.");
      }
    } catch (err) {
      setInsuranceMsg(err?.response?.data?.message || "Upload failed.");
    } finally {
      setLimitDocUploading(false);
    }
  };

  const saveInsurance = async () => {
    setInsuranceSaving(true);
    setInsuranceMsg("");
    try {
      const resp = await api.post("/v1/subcontractor/insurance_scaling", {
        minimums: insurance.minimums,
        labels: insurance.labels,
        limit_doc: insurance.limit_doc || null,
      });
      if (resp.status === 200) {
        const r = resp.rechecked ?? resp?.data?.rechecked;
        const d = resp.declined ?? resp?.data?.declined;
        setInsuranceMsg(
          typeof r === "number"
            ? `Saved. Re-evaluated ${r} record${r === 1 ? "" : "s"}; ${d ?? 0} now declined.`
            : "Saved.",
        );
        const start = fmtDate(historyCalendar[0].startDate);
        const end = fmtEndDate(historyCalendar[0].endDate);
        fetchList(searchTerm, start, end);
        setTimeout(() => setInsuranceMsg(""), 4500);
      } else {
        setInsuranceMsg(resp?.message || "Failed to save.");
      }
    } catch (err) {
      setInsuranceMsg(err?.response?.data?.message || "Failed to save.");
    } finally {
      setInsuranceSaving(false);
    }
  };

  // --- Grade scaling (admin-editable score → grade thresholds) -------------
  const [showScaling, setShowScaling] = useState(false);
  const [scaling, setScaling] = useState({
    a_min: 85,
    b_min: 70,
    labels: {
      A: "Approved",
      B: "Conditional Approval",
      C: "Not Approved",
    },
  });
  const [scalingSaving, setScalingSaving] = useState(false);
  const [scalingMsg, setScalingMsg] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const resp = await api.get("/v1/subcontractor/grade_scaling");
        if (resp.status === 200 && resp.data) setScaling(resp.data);
      } catch (err) {
        // Non-fatal — the modal still opens with defaults.
        console.warn("Could not load grade scaling config", err);
      }
    })();
  }, []);

  const saveScaling = async () => {
    const a = Number(scaling.a_min);
    const b = Number(scaling.b_min);
    if (!(b >= 0 && a > b && a <= 100)) {
      setScalingMsg("A threshold must be greater than B, and both in 0–100.");
      return;
    }
    setScalingSaving(true);
    setScalingMsg("");
    try {
      const resp = await api.post("/v1/subcontractor/grade_scaling", {
        a_min: a,
        b_min: b,
        labels: scaling.labels,
      });
      if (resp.status === 200) {
        const n = resp.recomputed ?? resp?.data?.recomputed;
        setScalingMsg(
          typeof n === "number"
            ? `Saved. Re-graded ${n} existing record${n === 1 ? "" : "s"}.`
            : "Saved. Applied to all existing and future submissions.",
        );
        // Refresh the table so updated grades/approvals appear immediately
        const start = fmtDate(historyCalendar[0].startDate);
        const end = fmtEndDate(historyCalendar[0].endDate);
        fetchList(searchTerm, start, end);
        setTimeout(() => setScalingMsg(""), 3500);
      } else {
        setScalingMsg(resp?.message || "Failed to save.");
      }
    } catch (err) {
      setScalingMsg(err?.response?.data?.message || "Failed to save.");
    } finally {
      setScalingSaving(false);
    }
  };

  // Documents come back as absolute server paths like
  // "/var/www/hub2.ntactops.com/ntact/public/assets/documents/avatar-2.jpg".
  // Strip everything up to and including "/public" so the remainder is a
  // URL path relative to the public web root.
  // Convert a stored path like
  //   /var/www/hub2.ntactops.com/ntact/public/assets/documents/avatar.jpg
  // to the public URL
  //   https://hub2.ntactops.com/ntact/files/documents/avatar.jpg
  const toPublicDocUrl = (p) => {
    if (!p) return "";
    const marker = "/public/assets/";
    const idx = p.indexOf(marker);
    if (idx >= 0)
      return `${DOCUMENT_PUBLIC_BASE}/${p.substring(idx + marker.length)}`;
    return p;
  };
  // Route image loads through the backend proxy (same pattern as viewAiF.jsx)
  const toDocUrl = (p) => {
    const original = toPublicDocUrl(p);
    if (!original) return "";
    return `${DOCUMENT_PROXY_BASE}${encodeURIComponent(original)}`;
  };
  const isImage = (p) =>
    /\.(png|jpe?g|gif|heic|heif|webp|bmp|svg)$/i.test(p || "");
  const docName = (p) => (p || "").split("/").pop();

  // Uploads are saved with filenames like "safetyProgram__foo.pdf". Parse the
  // leading tag so the UI can show which question the document belongs to.
  const DOC_QUESTION_LABELS = {
    // Safety page uploads
    safetyProgram: "Q1. Do you have a company safety program?",
    safetyManual: "Q2. Do you have a safety manual?",
    oshaCitation: "Q5. Has your company ever been issued an OSHA Citation?",
    thirdPartyMedical:
      "Q6. Do you currently have a third-party medical consortium for case management of injuries? (Proof of Service)",
    hseOrientation:
      "Q9. Do you have an HSE Orientation Program for new hires and newly hired or promoted supervisors?",
    // Attestation page enclosed documents
    enc_training_curriculum: "Enclosed — Employee Training Curriculum",
    enc_hse_manual_toc: "Enclosed — HSE Manual Table of Contents",
    enc_training_cards: "Enclosed — Employee Training Cards",
    enc_insurance_cert: "Enclosed — Certificate Of Insurance Details",
    enc_w9: "Enclosed — W9 Documents",
    enc_proof_of_service: "Enclosed — Proof of Service",
  };
  const parseDocMeta = (p) => {
    const name = docName(p);
    const sep = name.indexOf("__");
    if (sep > 0) {
      const tag = name.substring(0, sep);
      const originalName = name.substring(sep + 2);
      return {
        originalName,
        questionLabel: DOC_QUESTION_LABELS[tag] || tag,
      };
    }
    return { originalName: name, questionLabel: null };
  };

  // Toast
  const [toastOpen, setToastOpen] = useState(false);
  const [toastMsg, setToastMsg] = useState("");
  const [toastSeverity, setToastSeverity] = useState("info");

  // Filters
  const [selectedFilters, setSelectedFilters] = useState({
    statuses: [],
    grades: [],
    approvals: [],
  });
  // "active" (default), "archived", or "all". Lives outside selectedFilters
  // because it changes the server-side query rather than filtering the loaded
  // rows in memory.
  const [archivedView, setArchivedView] = useState("active");
  const [archivingId, setArchivingId] = useState(null);
  // The row pending confirmation in the styled archive modal. `null` when the
  // modal is closed.
  const [archiveTarget, setArchiveTarget] = useState(null);
  // The row whose status is being changed via the status popup. `null` when
  // the popup is closed. Admin-only.
  const [statusTarget, setStatusTarget] = useState(null);
  const [statusSaving, setStatusSaving] = useState(false);

  const [selectionRange, setSelectionRange] = useState([
    {
      startDate: dayjs().subtract(90, "day").toDate(),
      endDate: dayjs().toDate(),
      key: "selection",
    },
  ]);
  const [historyCalendar, setHistoryCalendar] = useState(selectionRange);

  // Format a dayjs/Date as YYYY-MM-DD for the API.
  const fmtDate = (d) => dayjs(d).format("YYYY-MM-DD");

  // The backend treats `end_date` as inclusive of the whole day, so the picked
  // end date is sent as-is. We push it forward by one day only as a safety net
  // against records whose `created_at` falls after midnight of the chosen day
  // when the backend is running an older build that does `<= end_date`.
  const fmtEndDate = (d) => dayjs(d).add(1, "day").format("YYYY-MM-DD");

  // --- Fetch -----------------------------------------------------------------
  const fetchList = async (term = "", startDate, endDate, archived = archivedView) => {
    try {
      setLoader(true);
      const params = {};
      if (term && term.trim()) params.q = term.trim();
      if (startDate && endDate) {
        params.start_date = startDate;
        params.end_date = endDate;
      }
      if (archived) params.archived = archived;
      const resp = await api.get("/v1/subcontractor/list", { params });
      if (resp.status === 200) {
        setRows(resp.data || []);
      } else {
        setRows([]);
      }
    } catch (err) {
      console.error("Error fetching subcontractors:", err);
      setRows([]);
    } finally {
      setLoader(false);
    }
  };

  // Debounced search
  useEffect(() => {
    const handle = setTimeout(() => {
      const start = fmtDate(historyCalendar[0].startDate);
      const end = fmtEndDate(historyCalendar[0].endDate);
      fetchList(searchTerm, start, end);
    }, 400);
    return () => clearTimeout(handle);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTerm]);

  // Refetch immediately whenever the archive view toggles.
  useEffect(() => {
    const start = fmtDate(historyCalendar[0].startDate);
    const end = fmtEndDate(historyCalendar[0].endDate);
    fetchList(searchTerm, start, end, archivedView);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [archivedView]);

  // Close popups on outside click
  useEffect(() => {
    const handle = (e) => {
      if (pickerRef.current && !pickerRef.current.contains(e.target))
        setShowPicker(false);
      if (filterRef.current && !filterRef.current.contains(e.target))
        setShowFilterPopup(false);
    };
    document.addEventListener("mousedown", handle);
    return () => document.removeEventListener("mousedown", handle);
  }, []);

  // Reset page when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [selectedFilters, rows]);

  // --- Date range actions ----------------------------------------------------
  const handleApply = () => {
    setHistoryCalendar(selectionRange);
    setShowPicker(false);
    const start = fmtDate(selectionRange[0].startDate);
    const end = fmtEndDate(selectionRange[0].endDate);
    fetchList(searchTerm, start, end);
  };

  const handleReset = () => {
    const def = [
      {
        startDate: dayjs().subtract(90, "day").toDate(),
        endDate: dayjs().toDate(),
        key: "selection",
      },
    ];
    setSelectionRange(def);
    setHistoryCalendar(def);
    setShowPicker(false);
    const start = fmtDate(def[0].startDate);
    const end = fmtEndDate(def[0].endDate);
    fetchList(searchTerm, start, end);
  };

  // --- Filtering -------------------------------------------------------------
  const toggleFilter = (category, value) => {
    setSelectedFilters((prev) => {
      const arr = prev[category];
      const updated = arr.includes(value)
        ? arr.filter((v) => v !== value)
        : [...arr, value];
      return { ...prev, [category]: updated };
    });
  };

  const clearAllFilters = () => {
    setSelectedFilters({ statuses: [], grades: [], approvals: [] });
    setArchivedView("active");
  };

  const activeFilterCount =
    selectedFilters.statuses.length +
    selectedFilters.grades.length +
    selectedFilters.approvals.length +
    (archivedView !== "active" ? 1 : 0);

  // --- Archive / un-archive --------------------------------------------------
  // Clicking the row's archive icon opens a styled confirmation modal. The
  // actual API call happens in `confirmArchive` once the user confirms.
  const handleArchiveToggle = (row) => {
    setArchiveTarget(row);
  };

  const confirmArchive = async () => {
    if (!archiveTarget) return;
    const row = archiveTarget;
    const willArchive = !row.is_archived;
    try {
      setArchivingId(row.id);
      const resp = await api.post("/v1/subcontractor/archive", {
        id: row.id,
        is_archived: willArchive,
      });
      if (resp.status === 200) {
        setToastSeverity("success");
        setToastMsg(willArchive ? "Submission archived." : "Submission un-archived.");
        setToastOpen(true);
        setArchiveTarget(null);
        const start = fmtDate(historyCalendar[0].startDate);
        const end = fmtEndDate(historyCalendar[0].endDate);
        fetchList(searchTerm, start, end, archivedView);
      } else {
        setToastSeverity("error");
        setToastMsg(resp?.message || "Failed to update archive state.");
        setToastOpen(true);
      }
    } catch (err) {
      setToastSeverity("error");
      setToastMsg(err?.response?.data?.message || "Failed to update archive state.");
      setToastOpen(true);
    } finally {
      setArchivingId(null);
    }
  };

  // --- Status change (admin only) ------------------------------------------
  // Clicking a row's Status badge opens a popup; `confirmStatusChange` sends
  // the new code to the backend. Marking a row "R" (Resubmit) lets the
  // subcontractor pull it back up via the retrieve-draft flow.
  const confirmStatusChange = async (newStatus) => {
    if (!statusTarget) return;
    const row = statusTarget;
    if (newStatus === row.status) {
      setStatusTarget(null);
      return;
    }
    try {
      setStatusSaving(true);
      const resp = await api.post("/v1/subcontractor/status", {
        id: row.id,
        status: newStatus,
      });
      if (resp.status === 200) {
        setToastSeverity("success");
        setToastMsg(`Status changed to ${statusLabelOf(newStatus)}.`);
        setToastOpen(true);
        setStatusTarget(null);
        const start = fmtDate(historyCalendar[0].startDate);
        const end = fmtEndDate(historyCalendar[0].endDate);
        fetchList(searchTerm, start, end, archivedView);
      } else {
        setToastSeverity("error");
        setToastMsg(resp?.message || "Failed to update status.");
        setToastOpen(true);
      }
    } catch (err) {
      setToastSeverity("error");
      setToastMsg(err?.response?.data?.message || "Failed to update status.");
      setToastOpen(true);
    } finally {
      setStatusSaving(false);
    }
  };

  const filteredRows = useMemo(() => {
    return rows.filter((r) => {
      const { statuses, grades, approvals } = selectedFilters;
      const grade = r.scorecard?.grade;
      const approval = r.scorecard?.approval_status;
      if (statuses.length && !statuses.includes(r.status)) return false;
      if (grades.length && !grades.includes(grade)) return false;
      if (approvals.length && !approvals.includes(approval)) return false;
      return true;
    });
  }, [rows, selectedFilters]);

  // --- Pagination ------------------------------------------------------------
  const totalPages = Math.ceil(filteredRows.length / rowsPerPage) || 1;
  const paginated = filteredRows.slice(
    (currentPage - 1) * rowsPerPage,
    currentPage * rowsPerPage,
  );

  // --- Row actions -----------------------------------------------------------
  const openView = (row, tab = "form") => {
    setInitialTab(tab);
    setSelectedRow(row);
  };
  const openEdit = (row) => {
    const phone = row.phone || "";
    sessionStorage.setItem("subcontractorSubId", row.id);
    sessionStorage.setItem("subcontractorToken", "admin");
    navigate(
      `${
        SUBCONTRACTOR_ROUTES.form
      }?mode=continue&allow=guest&returnTo=dashboard&phone=${encodeURIComponent(
        phone,
      )}`,
    );
  };

  // --- Render ---------------------------------------------------------------
  return (
    <SideNavLayout>
      <div
        className="flex flex-col min-h-[90vh] max-h-[90vh] p-2 overflow-y-auto"
        style={{ scrollbarWidth: "none" }}
      >
        <div className="flex items-start justify-between gap-4">
          <div>
            <div className="font-inter font-bold text-[24px]">Subcontractors</div>
            {/* <p className="text-sm text-gray-500 mb-3">
              Pre-qualification submissions with auto-computed safety scorecard.
            </p> */}
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setShowAnalytics(true)}
              className="shrink-0 flex items-center gap-2 border-2 border-[#E4E4E7] rounded-md px-3 py-2 bg-white text-sm text-black hover:bg-gray-50"
              title="Subcontractor analytics"
            >
              <InsightsIcon fontSize="small" />
              <span>Analytics</span>
            </button>
            {!isProjectManager && (
              <button
                onClick={() => {
                  // Open the unified Scaling modal on the Score tab. Both
                  // Score Scaling and Insurance Scaling now live behind this
                  // single trigger; switching is done via the tab strip at
                  // the top of either modal.
                  setShowScaling(true);
                  setShowInsurance(false);
                }}
                className="shrink-0 flex items-center gap-2 border-2 border-[#E4E4E7] rounded-md px-3 py-2 bg-white text-sm text-black hover:bg-gray-50"
                title="Score & Insurance scaling settings"
              >
                <TuneIcon fontSize="small" />
                <span>Scaling</span>
              </button>
            )}
          </div>
        </div>

        {/* Controls row */}
        <div className="flex justify-between gap-2 items-center mt-2">
          {/* Search */}
          <div className="relative flex-1 max-w-sm mr-auto">
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search name, email, phone..."
              className="w-full border-2 border-[#E4E4E7] rounded-md px-3 py-2 bg-white text-sm text-black focus:outline-none focus:border-[#8B1818]"
            />
            {searchTerm && (
              <button
                type="button"
                onClick={() => setSearchTerm("")}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 text-sm"
                aria-label="Clear search"
              >
                ✕
              </button>
            )}
          </div>

          {/* Date picker */}
          <div className="relative" ref={pickerRef}>
            <button
              onClick={() => setShowPicker(!showPicker)}
              className="flex items-center gap-2 border-2 border-[#E4E4E7] rounded-md px-3 py-2 bg-white text-sm text-black"
            >
              {calendericonblack}
              <span>
                {`${dayjs(historyCalendar[0].startDate).format(
                  "MMM D, YYYY",
                )} - ${dayjs(historyCalendar[0].endDate).format(
                  "MMM D, YYYY",
                )}`}
              </span>
            </button>
            {showPicker && (
              <div className="absolute right-0 mt-2 z-[9999] shadow-lg border rounded-md bg-white">
                <DateRangePicker
                  onChange={(item) => setSelectionRange([item.selection])}
                  ranges={selectionRange}
                  moveRangeOnFirstSelection={false}
                  maxDate={new Date()}
                />
                <div className="flex justify-end gap-2 p-3 border-t bg-gray-50">
                  <button
                    onClick={handleReset}
                    className="px-3 py-1 text-sm border rounded-md text-gray-600 hover:bg-gray-100"
                  >
                    Reset
                  </button>
                  <button
                    onClick={handleApply}
                    className="px-3 py-1 text-sm rounded-md bg-[#8B1818] text-white hover:bg-[#701313]"
                  >
                    Apply
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Filters */}
          <div className="relative" ref={filterRef}>
            <button
              onClick={() => setShowFilterPopup(!showFilterPopup)}
              className="flex items-center gap-2 border-2 border-[#E4E4E7] rounded-md px-3 py-2 bg-white text-sm text-black hover:bg-gray-50"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-4 w-4"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={2}
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z"
                />
              </svg>
              <span>Filters</span>
              {activeFilterCount > 0 && (
                <span className="bg-[#8B1818] text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
                  {activeFilterCount}
                </span>
              )}
            </button>
            {showFilterPopup && (
              <div className="absolute right-0 mt-2 z-[9999] shadow-xl border border-gray-200 rounded-xl bg-white w-[340px] overflow-hidden">
                <div className="flex items-center justify-between px-5 py-3 bg-gray-50 border-b border-gray-200">
                  <span className="text-sm font-semibold text-gray-800">
                    Filters
                  </span>
                  {activeFilterCount > 0 && (
                    <button
                      onClick={clearAllFilters}
                      className="text-xs font-medium text-[#8B1818] hover:underline"
                    >
                      Clear All ({activeFilterCount})
                    </button>
                  )}
                </div>
                <div className="max-h-[380px] overflow-y-auto px-5 py-4 space-y-5">
                  {/* Archive view — admins only. Project Managers always see
                      the default "Active" list and can't surface archived
                      submissions. */}
                  {!isProjectManager && (
                    <>
                      <div>
                        <p className="text-[11px] font-bold text-gray-400 uppercase tracking-wider mb-2">
                          Show
                        </p>
                        <div className="flex flex-wrap gap-2">
                          {[
                            { v: "active", label: "Active" },
                            { v: "archived", label: "Archived" },
                            { v: "all", label: "All (incl. archived)" },
                          ].map((opt) => {
                            const active = archivedView === opt.v;
                            return (
                              <button
                                key={opt.v}
                                onClick={() => setArchivedView(opt.v)}
                                className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-all ${
                                  active
                                    ? "bg-[#8B1818] text-white border-[#8B1818] shadow-sm"
                                    : "bg-white text-gray-600 border-gray-200 hover:border-gray-400"
                                }`}
                              >
                                {opt.label}
                              </button>
                            );
                          })}
                        </div>
                      </div>
                      <div className="border-t border-gray-100" />
                    </>
                  )}
                  {/* Status */}
                  <div>
                    <p className="text-[11px] font-bold text-gray-400 uppercase tracking-wider mb-2">
                      Submission Status
                    </p>
                    <div className="flex flex-wrap gap-2">
                      {STATUS_OPTIONS.map((s) => {
                        const active = selectedFilters.statuses.includes(
                          s.code,
                        );
                        return (
                          <button
                            key={s.code}
                            onClick={() => toggleFilter("statuses", s.code)}
                            className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-all ${
                              active
                                ? "bg-[#8B1818] text-white border-[#8B1818] shadow-sm"
                                : "bg-white text-gray-600 border-gray-200 hover:border-gray-400"
                            }`}
                          >
                            {s.label}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                  <div className="border-t border-gray-100" />
                  {/* Grade */}
                  <div>
                    <p className="text-[11px] font-bold text-gray-400 uppercase tracking-wider mb-2">
                      Grade
                    </p>
                    <div className="flex flex-wrap gap-2">
                      {GRADE_OPTIONS.map((g) => {
                        const active = selectedFilters.grades.includes(g);
                        const base = active
                          ? "bg-[#8B1818] text-white border-[#8B1818] shadow-sm"
                          : g === "A"
                          ? "bg-green-50 text-green-700 border-green-200 hover:border-green-400"
                          : g === "B"
                          ? "bg-yellow-50 text-yellow-700 border-yellow-200 hover:border-yellow-400"
                          : "bg-red-50 text-red-700 border-red-200 hover:border-red-400";
                        return (
                          <button
                            key={g}
                            onClick={() => toggleFilter("grades", g)}
                            className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-all ${base}`}
                          >
                            Grade {g}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                  <div className="border-t border-gray-100" />
                  {/* Approval */}
                  <div>
                    <p className="text-[11px] font-bold text-gray-400 uppercase tracking-wider mb-2">
                      Approval Status
                    </p>
                    <div className="flex flex-wrap gap-2">
                      {APPROVAL_OPTIONS.map((a) => {
                        const active = selectedFilters.approvals.includes(a);
                        return (
                          <button
                            key={a}
                            onClick={() => toggleFilter("approvals", a)}
                            className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-all ${
                              active
                                ? "bg-[#8B1818] text-white border-[#8B1818] shadow-sm"
                                : "bg-white text-gray-600 border-gray-200 hover:border-gray-400"
                            }`}
                          >
                            {a}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                </div>
                <div className="flex justify-end px-5 py-3 border-t border-gray-200 bg-gray-50">
                  <button
                    onClick={() => setShowFilterPopup(false)}
                    className="px-4 py-1.5 text-sm font-medium rounded-lg bg-[#8B1818] text-white hover:bg-[#701313] transition-colors"
                  >
                    Apply
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Table */}
        <div className="mt-4 mb-6">
          <div className="overflow-x-auto rounded-xl shadow-md border border-gray-200">
            <table className="w-full bg-white text-left rounded-xl">
              <thead className="bg-gradient-to-r from-slate-100 to-slate-200 text-[#475569] font-Font1 text-[11px] uppercase tracking-wider">
                <tr>
                  <th className="px-4 py-3.5">
                    <div className="flex items-center">
                      Name <KeyboardArrowDownIcon />
                    </div>
                  </th>
                  <th className="px-4 py-3.5">
                    <div className="flex items-center">
                      Company <KeyboardArrowDownIcon />
                    </div>
                  </th>
                  <th className="px-4 py-3.5">Phone</th>
                  <th className="px-4 py-3.5 text-center">Status</th>
                  <th className="px-4 py-3.5 text-center">Grade</th>
                  <th className="px-4 py-3.5 text-center">Score</th>
                  <th className="px-4 py-3.5 text-center">System Approval</th>
                  <th className="px-4 py-3.5 text-center">Admin Approval</th>
                  <th className="px-4 py-3.5 text-center">Insurance Expiry</th>
                  <th className="px-4 py-3.5 text-center">Actions</th>
                </tr>
              </thead>
              <tbody className="text-[#374151] text-sm">
                {paginated.length > 0 ? (
                  paginated.map((row, idx) => {
                    const grade = row.scorecard?.grade;
                    const total = row.scorecard?.total_score;
                    const approval = row.scorecard?.approval_status;
                    const statusLabel = statusLabelOf(row.status);
                    const fullName = `${row.firstname || ""} ${row.lastname || ""}`.trim() || "—";
                    return (
                      <tr
                        key={row.id}
                        className={`border-t border-gray-100 transition-colors hover:bg-[#FFF8F8] ${
                          idx % 2 === 0 ? "bg-white" : "bg-gray-50/40"
                        }`}
                      >
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="font-semibold text-gray-900">{fullName}</span>
                            {row.is_archived && (
                              <span
                                className="inline-flex items-center gap-0.5 px-1.5 py-0.5 text-[10px] font-semibold rounded-full bg-gray-200 text-gray-700 border border-gray-300"
                                title="Archived submission"
                              >
                                <Inventory2OutlinedIcon style={{ fontSize: 11 }} />
                                Archived
                              </span>
                            )}
                          </div>
                          {row.email && (
                            <div
                              className="text-[11px] text-gray-500 truncate max-w-[220px] leading-tight"
                              title={row.email}
                            >
                              {row.email}
                            </div>
                          )}
                          {row.created_at && (
                            <div className="text-[11px] text-gray-400 leading-tight mt-0.5">
                              Created {dayjs(row.created_at).format("MM-DD-YYYY")}
                            </div>
                          )}
                        </td>
                        <td className="px-4 py-3 text-gray-700">{row.company_name || "—"}</td>
                        <td className="px-4 py-3 tabular-nums text-gray-700">{row.phone || "—"}</td>
                        <td className="px-4 py-3 text-center">
                          {isProjectManager ? (
                            <span
                              className={`inline-block px-2.5 py-1 text-xs font-medium rounded-full border ${statusBadge(
                                row.status,
                              )}`}
                            >
                              {statusLabel}
                            </span>
                          ) : (
                            <button
                              type="button"
                              onClick={() => setStatusTarget(row)}
                              title="Click to change status"
                              className={`inline-flex items-center gap-1 px-2.5 py-1 text-xs font-medium rounded-full border transition hover:ring-2 hover:ring-offset-1 hover:ring-gray-300 ${statusBadge(
                                row.status,
                              )}`}
                            >
                              {statusLabel}
                              <KeyboardArrowDownIcon style={{ fontSize: 14 }} />
                            </button>
                          )}
                        </td>
                        <td className="px-4 py-3 text-center">
                          <span
                            className={`inline-block px-3 py-1 text-xs font-bold rounded-full border ${gradeBadge(
                              grade,
                            )}`}
                          >
                            {grade || "—"}
                          </span>
                        </td>
                        <td className="px-3 py-3 text-center font-semibold">
                          {typeof total === "number" ? (
                            <button
                              onClick={() => openView(row, "scorecard")}
                              className="text-[#8B1818] underline decoration-dotted underline-offset-2 hover:text-[#701313] focus:outline-none"
                              title="View scorecard"
                            >
                              {total}/100
                            </button>
                          ) : (
                            "—"
                          )}
                        </td>
                        <td className="px-4 py-3 text-center">
                          {approval ? (
                            <span
                              className={`inline-block px-2.5 py-1 text-xs font-medium rounded-full ${approvalBadge(
                                approval,
                              )}`}
                            >
                              {approval}
                            </span>
                          ) : (
                            "—"
                          )}
                        </td>
                        <td className="px-4 py-3 text-center">
                          {row.admin_approval ? (
                            <div className="flex flex-col items-center gap-0.5">
                              <span
                                className={`inline-block px-2.5 py-1 text-xs font-medium rounded-full ${adminApprovalBadge(
                                  row.admin_approval,
                                )}`}
                              >
                                {row.admin_approval}
                              </span>
                              {row.attestation?.insurance_decline_reason && (
                                <span
                                  className="text-[10px] text-red-600 max-w-[220px] truncate"
                                  title={row.attestation.insurance_decline_reason}
                                >
                                  Insurance below minimum
                                </span>
                              )}
                            </div>
                          ) : (
                            <span className="text-gray-400 text-xs">Pending</span>
                          )}
                        </td>
                        <td className="px-4 py-3 text-center">
                          {(() => {
                            const info = insuranceExpiryInfo(
                              row.attestation?.insuranceExpiryDate,
                            );
                            if (!info) {
                              return <span className="text-gray-400 text-xs">—</span>;
                            }
                            return (
                              <div className="flex flex-col items-center gap-0.5">
                                <span
                                  className={`inline-block px-2 py-0.5 text-[10px] font-semibold rounded-full border ${info.tone}`}
                                >
                                  {info.dateLabel}
                                </span>
                                <span className="text-[9px] text-gray-500">
                                  {info.hint}
                                </span>
                              </div>
                            );
                          })()}
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex gap-1 items-center justify-center">
                            <button
                              onClick={() => openView(row)}
                              className="bg-[#1E4976] w-[34px] text-white p-1.5 flex items-center justify-center rounded-md hover:bg-[#163659] shadow-sm transition"
                              title="View"
                            >
                              <RemoveRedEyeIcon style={{ width: 18 }} />
                            </button>
                            {!isProjectManager && (
                              <button
                                onClick={() => handleArchiveToggle(row)}
                                disabled={archivingId === row.id}
                                className={`w-[34px] p-1.5 flex items-center justify-center rounded-md shadow-sm transition disabled:opacity-50 ${
                                  row.is_archived
                                    ? "bg-emerald-700 text-white hover:bg-emerald-800"
                                    : "bg-gray-200 text-gray-700 hover:bg-gray-300"
                                }`}
                                title={row.is_archived ? "Un-archive" : "Archive"}
                              >
                                {row.is_archived ? (
                                  <UnarchiveOutlinedIcon style={{ width: 18 }} />
                                ) : (
                                  <Inventory2OutlinedIcon style={{ width: 18 }} />
                                )}
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })
                ) : (
                  <tr>
                    <td
                      colSpan="10"
                      className="px-6 py-10 text-center text-gray-500 text-sm"
                    >
                      No subcontractors found.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>

            {/* Pagination */}
            <div className="flex items-center justify-between mt-4 px-2 pb-2">
              <div className="flex items-center gap-2 text-sm py-2">
                <span>Rows per page:</span>
                <select
                  value={rowsPerPage}
                  onChange={(e) => {
                    setRowsPerPage(Number(e.target.value));
                    setCurrentPage(1);
                  }}
                  className="border rounded px-2 py-1"
                >
                  {[10, 20, 50, 100].map((size) => (
                    <option key={size} value={size}>
                      {size}
                    </option>
                  ))}
                </select>
              </div>
              <div className="flex items-center gap-3">
                <button
                  className="p-2 border rounded disabled:opacity-40 disabled:cursor-not-allowed hover:bg-gray-100"
                  disabled={currentPage === 1}
                  onClick={() => setCurrentPage((prev) => prev - 1)}
                >
                  <FontAwesomeIcon icon={faChevronLeft} />
                </button>
                <span className="text-sm">
                  Page {currentPage} of {totalPages}
                </span>
                <button
                  className="p-2 border rounded disabled:opacity-40 disabled:cursor-not-allowed hover:bg-gray-100"
                  disabled={currentPage === totalPages || totalPages === 0}
                  onClick={() => setCurrentPage((prev) => prev + 1)}
                >
                  <FontAwesomeIcon icon={faChevronRight} />
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <Backdrop
        sx={{ color: "#fff", zIndex: (t) => t.zIndex.drawer + 1 }}
        open={loader}
      >
        <CircularProgress color="inherit" />
      </Backdrop>

      {selectedRow && (
        <SubcontractorDetailModal
          data={selectedRow}
          initialTab={initialTab}
          onClose={() => setSelectedRow(null)}
          onRefresh={() => {
            const start = fmtDate(historyCalendar[0].startDate);
            const end = fmtEndDate(historyCalendar[0].endDate);
            fetchList(searchTerm, start, end);
          }}
        />
      )}

      {showAnalytics && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[9999] px-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-7xl max-h-[95vh] overflow-y-auto">
            <div className="flex items-center justify-between px-5 py-3 border-b sticky top-0 bg-white z-10">
              <div>
                <h3 className="text-lg font-semibold text-gray-900">Subcontractor Analytics</h3>
                <p className="text-xs text-gray-500">
                  Snapshot of {analytics.total} record{analytics.total === 1 ? "" : "s"} for the selected date range
                </p>
              </div>
              <button
                onClick={() => setShowAnalytics(false)}
                className="text-gray-500 hover:text-gray-800"
              >
                <CloseIcon />
              </button>
            </div>

            <div className="p-5 space-y-5">
              {/* KPI tiles */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                {[
                  { label: "Total", value: analytics.total, color: "#1E4976" },
                  { label: "Submitted", value: analytics.submitted, color: "#16A34A" },
                  { label: "Drafts", value: analytics.drafts, color: "#9CA3AF" },
                  {
                    label: "Avg Score",
                    value: analytics.avgScore !== null ? `${analytics.avgScore}/100` : "—",
                    color: "#8B1818",
                  },
                ].map((t) => (
                  <div
                    key={t.label}
                    className="rounded-xl border border-gray-100 bg-gray-50 p-4 text-left"
                    style={{ borderLeft: `4px solid ${t.color}` }}
                  >
                    <div className="text-xs uppercase tracking-wider text-gray-500">{t.label}</div>
                    <div className="mt-1 text-2xl font-bold text-[#0A0A0A]">{t.value}</div>
                  </div>
                ))}
              </div>

              {/* Distributions row */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                {/* Grade distribution */}
                <div className="rounded-xl border border-gray-200 p-4">
                  <h4 className="text-sm font-semibold text-gray-900 mb-3">Grade Distribution</h4>
                  <div className="space-y-2">
                    {[
                      { k: "A", color: "bg-green-500", text: "text-green-700" },
                      { k: "B", color: "bg-yellow-500", text: "text-yellow-700" },
                      { k: "C", color: "bg-red-500", text: "text-red-700" },
                      { k: "none", label: "Ungraded", color: "bg-gray-400", text: "text-gray-600" },
                    ].map((g) => {
                      const v = analytics.gradeCounts[g.k];
                      const pct = analytics.total ? (v / analytics.total) * 100 : 0;
                      return (
                        <div key={g.k}>
                          <div className="flex items-center justify-between text-xs mb-1">
                            <span className={`font-medium ${g.text}`}>{g.label || `Grade ${g.k}`}</span>
                            <span className="text-gray-600">{v} ({pct.toFixed(1)}%)</span>
                          </div>
                          <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                            <div className={`h-full ${g.color}`} style={{ width: `${pct}%` }} />
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>

                {/* System Approval */}
                <div className="rounded-xl border border-gray-200 p-4">
                  <h4 className="text-sm font-semibold text-gray-900 mb-3">System Approval</h4>
                  <div className="space-y-2">
                    {[
                      { k: "Approved", color: "bg-green-500" },
                      { k: "Conditional Approval", color: "bg-yellow-500" },
                      { k: "Not Approved", color: "bg-red-500" },
                      { k: "none", label: "Unscored", color: "bg-gray-400" },
                    ].map((g) => {
                      const v = analytics.approvalCounts[g.k];
                      const pct = analytics.total ? (v / analytics.total) * 100 : 0;
                      return (
                        <div key={g.k}>
                          <div className="flex items-center justify-between text-xs mb-1">
                            <span className="font-medium text-gray-700">{g.label || g.k}</span>
                            <span className="text-gray-600">{v} ({pct.toFixed(1)}%)</span>
                          </div>
                          <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                            <div className={`h-full ${g.color}`} style={{ width: `${pct}%` }} />
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>

                {/* Admin Approval */}
                <div className="rounded-xl border border-gray-200 p-4">
                  <h4 className="text-sm font-semibold text-gray-900 mb-3">Admin Approval</h4>
                  <div className="space-y-2">
                    {[
                      { k: "Approved", color: "bg-green-500" },
                      { k: "Needs More Info", color: "bg-amber-500" },
                      { k: "Declined", color: "bg-red-500" },
                      { k: "none", label: "Pending", color: "bg-gray-400" },
                    ].map((g) => {
                      const v = analytics.adminCounts[g.k];
                      const pct = analytics.total ? (v / analytics.total) * 100 : 0;
                      return (
                        <div key={g.k}>
                          <div className="flex items-center justify-between text-xs mb-1">
                            <span className="font-medium text-gray-700">{g.label || g.k}</span>
                            <span className="text-gray-600">{v} ({pct.toFixed(1)}%)</span>
                          </div>
                          <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                            <div className={`h-full ${g.color}`} style={{ width: `${pct}%` }} />
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>

              {/* Insurance status */}
              <div className="rounded-xl border border-gray-200 p-4">
                <h4 className="text-sm font-semibold text-gray-900 mb-3">Certificate of Insurance</h4>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  {[
                    { label: "Valid", value: analytics.insValid, tone: "bg-green-50 text-green-700 border-green-200" },
                    { label: "Expiring ≤ 30d", value: analytics.insSoon, tone: "bg-amber-50 text-amber-700 border-amber-200" },
                    { label: "Expired", value: analytics.insExpired, tone: "bg-red-50 text-red-700 border-red-200" },
                    { label: "Missing", value: analytics.insMissing, tone: "bg-gray-50 text-gray-700 border-gray-200" },
                  ].map((b) => (
                    <div key={b.label} className={`rounded-lg border ${b.tone} px-3 py-2 text-left`}>
                      <div className="text-[11px] uppercase tracking-wider opacity-80">{b.label}</div>
                      <div className="text-xl font-bold mt-0.5">{b.value}</div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Scorecard distribution + HSE Stats */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {/* Scorecard score histogram */}
                <div className="rounded-xl border border-gray-200 p-4">
                  <h4 className="text-sm font-semibold text-gray-900">Scorecard Distribution</h4>
                  <p className="text-[11px] text-gray-500 mb-3">
                    Subcontractors grouped by total score (10-point buckets) ·{" "}
                    {analytics.avgScore !== null ? `Avg ${analytics.avgScore}/100` : "no scored records"}
                  </p>
                  <div className="flex items-end gap-2 h-44">
                    {analytics.scoreBuckets.map((b) => {
                      const h = (b.count / analytics.scoreBucketMax) * 100;
                      // Color buckets by grade thresholds (defaults A>=85, B>=70)
                      const lo = parseInt(b.key.split("-")[0], 10);
                      const aMin = Number(scaling.a_min) || 85;
                      const bMin = Number(scaling.b_min) || 70;
                      const tone =
                        lo >= aMin
                          ? "from-green-400 to-green-600"
                          : lo >= bMin
                          ? "from-yellow-300 to-yellow-500"
                          : "from-red-400 to-red-600";
                      return (
                        <div key={b.key} className="flex-1 flex flex-col items-center justify-end gap-1">
                          <div className="text-[10px] text-gray-700 font-semibold">
                            {b.count > 0 ? b.count : ""}
                          </div>
                          <div
                            className={`w-full bg-gradient-to-t ${tone} rounded-t`}
                            style={{ height: `${h}%`, minHeight: b.count > 0 ? 3 : 0 }}
                          />
                          <div className="text-[10px] text-gray-500">{b.label}</div>
                        </div>
                      );
                    })}
                  </div>
                  <div className="mt-3 flex items-center gap-3 text-[10px] text-gray-500">
                    <span className="inline-flex items-center gap-1">
                      <span className="w-3 h-3 rounded-sm bg-red-500" /> &lt; B threshold
                    </span>
                    <span className="inline-flex items-center gap-1">
                      <span className="w-3 h-3 rounded-sm bg-yellow-400" /> B
                    </span>
                    <span className="inline-flex items-center gap-1">
                      <span className="w-3 h-3 rounded-sm bg-green-500" /> A
                    </span>
                  </div>
                </div>

                {/* HSE Stats — TRIR by year + KPIs */}
                <div className="rounded-xl border border-gray-200 p-4">
                  <h4 className="text-sm font-semibold text-gray-900">HSE Stats — Avg TRIR by Year</h4>
                  <p className="text-[11px] text-gray-500 mb-3">
                    Total Recordable Incident Rate · lower is better
                  </p>
                  <div className="flex items-end gap-4 h-32 px-2">
                    {analytics.trirByYear.map((t) => {
                      const h = (t.avg / analytics.trirMax) * 100;
                      return (
                        <div
                          key={t.year}
                          className="flex-1 flex flex-col items-center justify-end gap-1"
                          title={`Avg across ${t.n} record${t.n === 1 ? "" : "s"}`}
                        >
                          <div className="text-[11px] font-semibold text-gray-700">
                            {t.avg.toFixed(2)}
                          </div>
                          <div
                            className="w-full rounded-t bg-gradient-to-t from-[#1E4976] to-[#3B82F6]"
                            style={{ height: `${h}%`, minHeight: t.avg > 0 ? 4 : 0 }}
                          />
                          <div className="text-[11px] text-gray-500">{t.year}</div>
                        </div>
                      );
                    })}
                  </div>
                  <div className="mt-3 grid grid-cols-3 gap-2 text-center">
                    {[
                      {
                        label: "Avg EMR",
                        value: analytics.avgEmr !== null ? analytics.avgEmr : "—",
                        tone: "bg-indigo-50 text-indigo-700 border-indigo-100",
                      },
                      {
                        label: "Fatalities (3y)",
                        value: analytics.totalFatalities,
                        tone: "bg-red-50 text-red-700 border-red-100",
                      },
                      {
                        label: "Serious (3y)",
                        value: analytics.totalSerious,
                        tone: "bg-amber-50 text-amber-700 border-amber-100",
                      },
                    ].map((k) => (
                      <div key={k.label} className={`rounded-lg border ${k.tone} px-2 py-2`}>
                        <div className="text-[10px] uppercase tracking-wider opacity-80">
                          {k.label}
                        </div>
                        <div className="text-lg font-bold">{k.value}</div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Submissions per month + Top companies */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div className="rounded-xl border border-gray-200 p-4">
                  <h4 className="text-sm font-semibold text-gray-900 mb-3">Submissions per Month (last 6)</h4>
                  <div className="flex items-end gap-3 h-40">
                    {analytics.months.map((m) => {
                      const h = (m.count / analytics.monthMax) * 100;
                      return (
                        <div key={m.key} className="flex-1 flex flex-col items-center justify-end gap-1">
                          <div className="text-[11px] text-gray-700 font-semibold">{m.count}</div>
                          <div
                            className="w-full bg-gradient-to-t from-[#8B1818] to-[#C24545] rounded-t"
                            style={{ height: `${h}%`, minHeight: m.count > 0 ? 4 : 0 }}
                          />
                          <div className="text-[10px] text-gray-500">{m.label}</div>
                        </div>
                      );
                    })}
                  </div>
                </div>

                <div className="rounded-xl border border-gray-200 p-4">
                  <h4 className="text-sm font-semibold text-gray-900 mb-3">Top Companies</h4>
                  {analytics.topCompanies.length === 0 ? (
                    <div className="text-xs text-gray-400">No company data yet.</div>
                  ) : (
                    <ul className="space-y-2">
                      {analytics.topCompanies.map(([name, count]) => (
                        <li key={name} className="flex items-center justify-between text-sm">
                          <span className="text-gray-800 truncate">{name}</span>
                          <span className="text-xs font-semibold text-[#8B1818] bg-red-50 rounded-full px-2 py-0.5">
                            {count}
                          </span>
                        </li>
                      ))}
                    </ul>
                  )}
                  {analytics.avgScore !== null && (
                    <div className="mt-4 pt-3 border-t border-gray-100 text-xs text-gray-600">
                      Score range: <b>{analytics.scoreMin}</b> – <b>{analytics.scoreMax}</b> · Average{" "}
                      <b>{analytics.avgScore}/100</b>
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className="flex justify-end px-5 py-3 border-t border-gray-200 bg-gray-50 sticky bottom-0">
              <button
                onClick={() => setShowAnalytics(false)}
                className="px-4 py-1.5 text-sm font-medium rounded-md bg-[#8B1818] text-white hover:bg-[#701313]"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {showInsurance && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[9999] px-4">
          <div className="bg-white rounded-xl shadow-lg w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between px-5 py-3 border-b sticky top-0 bg-white z-10">
              <div>
                <h3 className="text-lg font-semibold text-gray-900">Scaling</h3>
                {/* <p className="text-xs text-gray-500">
                  Manage Score thresholds and Insurance minimums.
                </p> */}
              </div>
              <button
                onClick={() => setShowInsurance(false)}
                className="text-gray-500 hover:text-gray-800"
              >
                <CloseIcon />
              </button>
            </div>

            {/* Same tab strip as the Score modal — clicking back switches the
                user from the Insurance form to the Score form without losing
                their place in the unified Scaling dialog. */}
            <div className="flex items-center border-b border-gray-200 px-5 pt-3 gap-1 sticky top-[57px] bg-white z-10">
              <button
                type="button"
                className="px-3 py-2 text-sm font-medium border-b-2 border-transparent text-gray-500 hover:text-gray-800"
                onClick={() => {
                  setShowInsurance(false);
                  setShowScaling(true);
                }}
              >
                Score Scaling
              </button>
              <button
                type="button"
                className="px-3 py-2 text-sm font-medium border-b-2 border-[#8B1818] text-[#8B1818]"
                onClick={() => {}}
              >
                Insurance Scaling
              </button>
            </div>

            <div className="p-5 space-y-3">
              {Object.keys(insurance.minimums || {}).length === 0 ? (
                <div className="text-sm text-gray-500">Loading…</div>
              ) : (
                Object.keys(insurance.minimums).map((k) => (
                  <div
                    key={k}
                    className="flex items-center justify-between gap-3 py-2 border-b border-gray-100 last:border-b-0"
                  >
                    <div className="flex-1 text-left">
                      <div className="text-sm text-gray-900 font-medium">
                        {insurance.labels?.[k] || k}
                      </div>
                    </div>
                    <div className="relative w-44">
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-xs text-gray-500">$</span>
                      <input
                        type="number"
                        min={0}
                        value={insurance.minimums[k]}
                        onChange={(e) =>
                          setInsurance((s) => ({
                            ...s,
                            minimums: {
                              ...s.minimums,
                              [k]: e.target.value,
                            },
                          }))
                        }
                        className="w-full pl-6 pr-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:border-gray-500"
                      />
                    </div>
                  </div>
                ))
              )}

              {/* Required Insurance Limits reference document. Subcontractors
                  see this file via the "Check Limits" button on their form. */}
              <div className="pt-4 mt-2 border-t border-gray-200">
                <div className="text-sm font-semibold text-gray-900">
                  Required Insurance Limits Document
                </div>
                <p className="text-xs text-gray-500 mt-0.5 mb-2">
                  Uploaded here, shown to subcontractors under “Check Limits” on
                  the Certificate Of Insurance step.
                </p>
                <div className="flex items-center gap-3 flex-wrap">
                  <label className="px-3 py-1.5 text-xs font-medium rounded-md border border-gray-300 bg-white hover:bg-gray-100 cursor-pointer">
                    {limitDocUploading
                      ? "Uploading…"
                      : insurance.limit_doc
                      ? "Replace Document"
                      : "Upload Document"}
                    <input
                      type="file"
                      accept=".pdf,image/*"
                      className="hidden"
                      disabled={limitDocUploading}
                      onChange={handleLimitDocUpload}
                    />
                  </label>
                  {insurance.limit_doc?.file_name && (
                    <>
                      <a
                        href={`${DOCUMENT_PUBLIC_BASE}/forms_files/${insurance.limit_doc.file_name}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-xs text-blue-700 hover:underline truncate max-w-[220px]"
                        title={insurance.limit_doc.file_name}
                      >
                        {insurance.limit_doc.file_name}
                      </a>
                      <button
                        type="button"
                        onClick={() =>
                          setInsurance((s) => ({ ...s, limit_doc: null }))
                        }
                        className="text-xs text-red-600 hover:underline"
                      >
                        Remove
                      </button>
                    </>
                  )}
                  {!insurance.limit_doc?.file_name && (
                    <span className="text-xs text-gray-400">
                      No document uploaded.
                    </span>
                  )}
                </div>
              </div>

              {insuranceMsg && (
                <div className="text-sm text-gray-700 pt-1">{insuranceMsg}</div>
              )}
            </div>

            <div className="flex justify-end gap-2 px-5 py-3 border-t border-gray-200 bg-gray-50 sticky bottom-0">
              <button
                onClick={() => setShowInsurance(false)}
                className="px-4 py-1.5 text-sm font-medium rounded-md border border-gray-300 bg-white hover:bg-gray-100"
              >
                Cancel
              </button>
              <button
                onClick={saveInsurance}
                disabled={insuranceSaving || limitDocUploading}
                className="px-4 py-1.5 text-sm font-medium rounded-md bg-[#8B1818] text-white hover:bg-[#701313] disabled:opacity-60"
              >
                {insuranceSaving ? "Saving..." : "Save Insurance Scaling"}
              </button>
            </div>
          </div>
        </div>
      )}

      {showScaling && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[9999] px-4">
          <div className="bg-white rounded-xl shadow-lg w-full max-w-lg overflow-hidden">
            <div className="flex items-center justify-between px-5 py-3 border-b">
              <div>
                <h3 className="text-lg font-semibold text-gray-900">Scaling</h3>
                {/* <p className="text-xs text-gray-500">
                  Manage Score thresholds and Insurance minimums.
                </p> */}
              </div>
              <button
                onClick={() => setShowScaling(false)}
                className="text-gray-500 hover:text-gray-800"
              >
                <CloseIcon />
              </button>
            </div>

            {/* Shared tab strip — clicking "Insurance Scaling" closes this
                modal and opens the insurance one (same tab strip lives there
                so the user can pop back without losing context). */}
            <div className="flex items-center border-b border-gray-200 px-5 pt-3 gap-1">
              <button
                type="button"
                className="px-3 py-2 text-sm font-medium border-b-2 border-[#8B1818] text-[#8B1818]"
                onClick={() => {}}
              >
                Score Scaling
              </button>
              <button
                type="button"
                className="px-3 py-2 text-sm font-medium border-b-2 border-transparent text-gray-500 hover:text-gray-800"
                onClick={() => {
                  setShowScaling(false);
                  setShowInsurance(true);
                }}
              >
                Insurance Scaling
              </button>
            </div>

            <div className="p-5 space-y-5">
              {/* Threshold inputs */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-semibold text-gray-600 uppercase tracking-wider mb-1">
                    Grade A — minimum score
                  </label>
                  <input
                    type="number"
                    min={1}
                    max={100}
                    value={scaling.a_min}
                    onChange={(e) =>
                      setScaling((s) => ({ ...s, a_min: e.target.value }))
                    }
                    className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:border-gray-500"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-600 uppercase tracking-wider mb-1">
                    Grade B — minimum score
                  </label>
                  <input
                    type="number"
                    min={0}
                    max={99}
                    value={scaling.b_min}
                    onChange={(e) =>
                      setScaling((s) => ({ ...s, b_min: e.target.value }))
                    }
                    className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:border-gray-500"
                  />
                </div>
              </div>

              {/* Derived ranges preview */}
              <div className="rounded-lg border border-gray-200 divide-y text-sm">
                <div className="flex items-center justify-between px-4 py-2">
                  <div className="flex items-center gap-3">
                    <span className="inline-flex w-8 h-8 items-center justify-center rounded-full bg-green-100 text-green-800 font-bold border border-green-200">
                      A
                    </span>
                    <span className="text-gray-700">
                      Score ≥ <span className="font-semibold">{scaling.a_min}</span>
                    </span>
                  </div>
                  <input
                    type="text"
                    value={scaling.labels?.A || ""}
                    onChange={(e) =>
                      setScaling((s) => ({
                        ...s,
                        labels: { ...s.labels, A: e.target.value },
                      }))
                    }
                    className="border border-gray-200 rounded-md px-2 py-1 text-xs w-44 text-right focus:outline-none focus:border-gray-500"
                  />
                </div>
                <div className="flex items-center justify-between px-4 py-2">
                  <div className="flex items-center gap-3">
                    <span className="inline-flex w-8 h-8 items-center justify-center rounded-full bg-yellow-100 text-yellow-800 font-bold border border-yellow-200">
                      B
                    </span>
                    <span className="text-gray-700">
                      <span className="font-semibold">{scaling.b_min}</span> ≤ Score &lt;{" "}
                      <span className="font-semibold">{scaling.a_min}</span>
                    </span>
                  </div>
                  <input
                    type="text"
                    value={scaling.labels?.B || ""}
                    onChange={(e) =>
                      setScaling((s) => ({
                        ...s,
                        labels: { ...s.labels, B: e.target.value },
                      }))
                    }
                    className="border border-gray-200 rounded-md px-2 py-1 text-xs w-44 text-right focus:outline-none focus:border-gray-500"
                  />
                </div>
                <div className="flex items-center justify-between px-4 py-2">
                  <div className="flex items-center gap-3">
                    <span className="inline-flex w-8 h-8 items-center justify-center rounded-full bg-red-100 text-red-800 font-bold border border-red-200">
                      C
                    </span>
                    <span className="text-gray-700">
                      Score &lt; <span className="font-semibold">{scaling.b_min}</span>
                    </span>
                  </div>
                  <input
                    type="text"
                    value={scaling.labels?.C || ""}
                    onChange={(e) =>
                      setScaling((s) => ({
                        ...s,
                        labels: { ...s.labels, C: e.target.value },
                      }))
                    }
                    className="border border-gray-200 rounded-md px-2 py-1 text-xs w-44 text-right focus:outline-none focus:border-gray-500"
                  />
                </div>
              </div>

              {scalingMsg && (
                <div className="text-sm text-gray-700">{scalingMsg}</div>
              )}
            </div>

            <div className="flex justify-end gap-2 px-5 py-3 border-t border-gray-200 bg-gray-50">
              <button
                onClick={() => setShowScaling(false)}
                className="px-4 py-1.5 text-sm font-medium rounded-md border border-gray-300 bg-white hover:bg-gray-100"
              >
                Cancel
              </button>
              <button
                onClick={saveScaling}
                disabled={scalingSaving}
                className="px-4 py-1.5 text-sm font-medium rounded-md bg-[#8B1818] text-white hover:bg-[#701313] disabled:opacity-60"
              >
                {scalingSaving ? "Saving..." : "Save Scaling"}
              </button>
            </div>
          </div>
        </div>
      )}

      {archiveTarget && (() => {
        const willArchive = !archiveTarget.is_archived;
        const displayName =
          archiveTarget.company_name ||
          `${archiveTarget.firstname || ""} ${archiveTarget.lastname || ""}`.trim() ||
          archiveTarget.email ||
          `Submission #${archiveTarget.id}`;
        const isBusy = archivingId === archiveTarget.id;
        return (
          <div
            className="fixed inset-0 bg-black/50 flex items-center justify-center z-[10000] px-4"
            onClick={() => !isBusy && setArchiveTarget(null)}
          >
            <div
              className="bg-white rounded-xl shadow-2xl w-full max-w-md overflow-hidden"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-start gap-3 px-5 pt-5 pb-3 border-b border-gray-100">
                <div
                  className={`shrink-0 w-10 h-10 rounded-full flex items-center justify-center ${
                    willArchive
                      ? "bg-amber-100 text-amber-700"
                      : "bg-emerald-100 text-emerald-700"
                  }`}
                >
                  {willArchive ? (
                    <Inventory2OutlinedIcon />
                  ) : (
                    <UnarchiveOutlinedIcon />
                  )}
                </div>
                <div className="flex-1">
                  <h3 className="text-base font-semibold text-gray-900 text-left">
                    {willArchive ? "Archive submission?" : "Un-archive submission?"}
                  </h3>
                  <p className="text-sm text-gray-600 mt-1 text-left">
                    {willArchive
                      ? "It will be hidden from the default dashboard view. You can restore it later from the Filters → Archived view."
                      : "It will appear in the default dashboard view again."}
                  </p>
                </div>
              </div>

              <div className="px-5 py-4">
                <div className="rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 text-left">
                  <div className="text-[11px] uppercase tracking-wider text-gray-500">
                    Subcontractor
                  </div>
                  <div className="text-sm font-medium text-gray-900 mt-0.5 break-words">
                    {displayName}
                  </div>
                  {archiveTarget.email && (
                    <div className="text-xs text-gray-500 mt-0.5 break-words">
                      {archiveTarget.email}
                    </div>
                  )}
                </div>
              </div>

              <div className="flex justify-end gap-2 px-5 py-3 border-t border-gray-200 bg-gray-50">
                <button
                  onClick={() => setArchiveTarget(null)}
                  disabled={isBusy}
                  className="px-4 py-1.5 text-sm font-medium rounded-md border border-gray-300 bg-white text-gray-700 hover:bg-gray-100 disabled:opacity-60"
                >
                  Cancel
                </button>
                <button
                  onClick={confirmArchive}
                  disabled={isBusy}
                  className={`px-4 py-1.5 text-sm font-medium rounded-md text-white disabled:opacity-60 ${
                    willArchive
                      ? "bg-[#8B1818] hover:bg-[#701313]"
                      : "bg-emerald-700 hover:bg-emerald-800"
                  }`}
                >
                  {isBusy
                    ? willArchive ? "Archiving…" : "Restoring…"
                    : willArchive ? "Archive" : "Un-archive"}
                </button>
              </div>
            </div>
          </div>
        );
      })()}

      {/* Status-change popup — admin only. Lets a reviewer move a submission
          between Draft / Submitted / Resubmit. Marking it "Resubmit" sends it
          back to the subcontractor's retrieve-draft flow so they can edit and
          re-submit. */}
      {statusTarget && (() => {
        const displayName =
          statusTarget.company_name ||
          `${statusTarget.firstname || ""} ${statusTarget.lastname || ""}`.trim() ||
          statusTarget.email ||
          `Submission #${statusTarget.id}`;
        return (
          <div
            className="fixed inset-0 bg-black/50 flex items-center justify-center z-[10000] px-4"
            onClick={() => !statusSaving && setStatusTarget(null)}
          >
            <div
              className="bg-white rounded-xl shadow-2xl w-full max-w-md overflow-hidden"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="px-5 pt-5 pb-3 border-b border-gray-100">
                <h3 className="text-base font-semibold text-gray-900 text-left">
                  Change status
                </h3>
                {/* <p className="text-sm text-gray-600 mt-1 text-left">
                  Updating status for{" "}
                  <span className="font-medium text-gray-900">{displayName}</span>.
                  Marking <b>Resubmit</b> sends it back to the subcontractor to
                  edit and re-submit.
                </p> */}
              </div>

              <div className="px-5 py-4 space-y-2">
                {STATUS_OPTIONS.map((opt) => {
                  const active = statusTarget.status === opt.code;
                  return (
                    <button
                      key={opt.code}
                      type="button"
                      disabled={statusSaving}
                      onClick={() => confirmStatusChange(opt.code)}
                      className={`w-full flex items-center justify-between px-4 py-2.5 rounded-lg border text-sm font-medium transition disabled:opacity-60 ${
                        active
                          ? "border-[#8B1818] bg-[#8B1818]/5 text-[#8B1818]"
                          : "border-gray-200 bg-white text-gray-700 hover:bg-gray-50 hover:border-gray-300"
                      }`}
                    >
                      <span className="flex items-center gap-2">
                        <span
                          className={`inline-block px-2 py-0.5 text-xs rounded-full border ${statusBadge(
                            opt.code,
                          )}`}
                        >
                          {opt.label}
                        </span>
                      </span>
                      {active && (
                        <span className="text-xs text-gray-500">Current</span>
                      )}
                    </button>
                  );
                })}
              </div>

              <div className="flex justify-end gap-2 px-5 py-3 border-t border-gray-200 bg-gray-50">
                <button
                  onClick={() => setStatusTarget(null)}
                  disabled={statusSaving}
                  className="px-4 py-1.5 text-sm font-medium rounded-md border border-gray-300 bg-white text-gray-700 hover:bg-gray-100 disabled:opacity-60"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        );
      })()}

      <ToastMessage
        message={toastMsg}
        severity={toastSeverity}
        open={toastOpen}
        handleClose={() => setToastOpen(false)}
      />
    </SideNavLayout>
  );
};

export default SubcontractorDashboard;
